

import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BiSearch, BiUserPlus, BiUser, BiPhoneCall } from 'react-icons/bi';
import AddCustomerPopup from './clients/AddCustomerPopup';
import EditCustomerPopup from './clients/EditCustomerPopup';
import CustomerTable from './clients/CustomerTable';
import Pagination from './clients/Pagination';

const TotalCustomerGrid = ({ user, setView }) => {
  const [customers, setCustomers] = useState([]);
  const [totalCalls, setTotalCalls] = useState(0);
  const [callData, setCallData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 8;

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const customersResponse = await fetch(`${process.env.REACT_APP_SERVER_API}/api/users`);
      const customersData = await customersResponse.json();
      const filteredCustomers = customersData.filter(user => user.role?.toLowerCase() !== "admin");
      setCustomers(filteredCustomers);

      const callsResponse = await fetch(`${process.env.REACT_APP_SERVER_API}/api/call-history?userId=${user.id}&role=${user.role}`);
      const callsData = await callsResponse.json();
      setCallData(callsData);
      setTotalCalls(callsData.length);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleAddSuccess = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setIsAddPopupOpen(false);
    toast.success('Client added successfully!');
    fetchData();
  };

  const handleEditSuccess = (updatedCustomer) => {
    setCustomers(prev => prev.map(cust => cust._id === updatedCustomer._id ? updatedCustomer : cust));
    setIsEditPopupOpen(false);
    setEditCustomer(null);
    toast.success('Client updated successfully!');
    fetchData();
  };

  const handleDelete = async (customerId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_API}/api/deleteUser/${customerId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setCustomers(prev => prev.filter(cust => cust._id !== customerId));
        toast.success('Client deleted successfully!');
        fetchData(); // Refresh to ensure stats are updated
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to delete client');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Something went wrong!');
    }
  };

  const getCustomerCalls = (customerId) => callData.filter(call => call.userId === customerId).length;

  const filteredCustomersList = useMemo(() => {
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.mobileNumber?.includes(searchTerm)
    );
  }, [customers, searchTerm]);

  const totalPages = Math.ceil(filteredCustomersList.length / itemsPerPage);
  const paginatedCustomers = filteredCustomersList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="text-lg font-medium text-gray-700">Loading Client Dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto bg-gray-50/50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Client Management</h2>
            <p className="text-gray-500 mt-1">Manage and monitor your client base and their call usage.</p>
          </div>
          <button
            onClick={() => setIsAddPopupOpen(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-6 rounded-xl shadow-lg shadow-indigo-200 transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <BiUserPlus size={24} />
            <span>Add New Client</span>
          </button>
        </div>

        {/* Stats and Search Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow">
            <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
              <BiUser size={32} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Clients</p>
              <p className="text-3xl font-bold text-gray-900">{customers.length}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow">
            <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
              <BiPhoneCall size={32} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total System Calls</p>
              <p className="text-3xl font-bold text-gray-900">{totalCalls}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center">
            <div className="relative w-full">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <CustomerTable
          customers={paginatedCustomers}
          getCustomerCalls={getCustomerCalls}
          onEdit={customer => { setEditCustomer(customer); setIsEditPopupOpen(true); }}
          onDelete={handleDelete}
          startIndex={(currentPage - 1) * itemsPerPage}
        />

        <div className="p-4 border-t border-gray-50">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredCustomersList.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>

      {isAddPopupOpen && <AddCustomerPopup onClose={() => setIsAddPopupOpen(false)} onSuccess={handleAddSuccess} />}
      {isEditPopupOpen && editCustomer && (
        <EditCustomerPopup
          customer={editCustomer}
          onClose={() => setIsEditPopupOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default TotalCustomerGrid;